import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AccountAssignmentResult, ClientAccount } from '../api/accountAssignmentApi';
import { useClientAccounts } from '../api/accountAssignmentApi';
import { useCourses, useDeleteCourseMutation, type CourseListResponseItem } from '../api/courseRecordApi';
import { AccountAssignmentPanel } from '../components/AccountAssignmentPanel';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';
import { Toast } from '../components/Toast';
import { ListPagination } from '../components/table/ListPagination';
import { SortableHeader } from '../components/table/SortableHeader';
import {
  TagFilterChipRow,
  TagFilterControl,
  TagNameList,
  describeActiveTagFilters,
  type TagFilterOption,
} from '../components/tagFilter/TagFilterControl';
import { EditTagsDialog } from '../components/tagPicker/EditTagsDialog';
import { WorkflowStatusBadge, deriveBadgeState } from '../components';
import { useClientContext } from '../hooks/useClientContext';
import { useListControls, usePageBoundsReset } from '../hooks/useListControls';
import { useRememberListPath } from '../hooks/useListPathMemory';
import { useToast } from '../hooks/useToast';
import { canDelete, describeDeleteBlock } from '../utils/deleteEligibility';
import { StandardTitleLine } from '../components/StandardTitleLine';
import {
  AdminIcon,
  AdminPageHeader,
  AdminRowMenu,
  AdminStatusMessage,
  adminActionIcons,
  createAdminListStyles,
  useAdminNarrowViewport,
} from '../admin-ui';
import { formatCourseHumanId } from '../utils/humanIds';
import { useTagsQuery } from '../api/tagApi';

function formatAssignedTo(ids: string[], accounts: ClientAccount[] | undefined): string {
  if (ids.length === 0) return '0 accounts';
  if (ids.length === 1) return accounts?.find((a) => a.id === ids[0])?.name ?? '1 account';
  return `${ids.length} accounts`;
}

type CourseSortKey = 'id' | 'title' | 'workflowState' | 'createdAt';

const COURSE_SORT_KEYS: readonly CourseSortKey[] = ['id', 'title', 'workflowState', 'createdAt'];

function formatCourseCreatedAt(createdAt: string): string {
  if (!createdAt) return '-';

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '-';

  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${date.getUTCDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function CourseListPage() {
  const navigate = useNavigate();
  useRememberListPath('/courses');
  const { clientContext } = useClientContext();
  const isStandardContext = clientContext === null;
  const { data: clientAccounts } = useClientAccounts();
  const isNarrow = useAdminNarrowViewport();
  const styles = useMemo(() => createAdminListStyles({ narrow: isNarrow }), [isNarrow]);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  // The kebab trigger outlives the menu item that opens a dialog, so it is the
  // only stable focus target: opening a dialog unmounts the clicked item in the
  // same commit, and ModalShell would otherwise capture <body> and restore
  // focus nowhere on close.
  const kebabTriggerRef = useRef<HTMLButtonElement | null>(null);
  const deleteReasonIdPrefix = useId();
  const [openActionCourseId, setOpenActionCourseId] = useState<string | null>(null);
  const [editTagsCourse, setEditTagsCourse] = useState<{ id: string; title: string } | null>(null);
  const { message: toastMessage, showToast } = useToast();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [courseActionError, setCourseActionError] = useState<string | null>(null);
  const {
    searchInput,
    setSearchInput,
    sortKey,
    sortDir,
    handleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    tagIds,
    setTagIds,
    queryParams,
    hasNonDefaultState,
    resetToDefaults,
  } = useListControls<CourseSortKey>({
    defaultSort: 'createdAt',
    sortKeys: COURSE_SORT_KEYS,
    assignedAccountId: clientContext?.id,
    sortStorageKey: '/courses',
  });
  const { data: tagLibrary } = useTagsQuery({ assignedAccountId: clientContext?.id });
  const tagFilterOptions: TagFilterOption[] = useMemo(
    () => (tagLibrary?.items ?? []).map((tag) => ({ id: tag.id, name: tag.name, usageCount: tag.courseCount })),
    [tagLibrary],
  );
  const [assignmentPanel, setAssignmentPanel] = useState<{
    id: string;
    title: string;
    initialFilter: 'notAssigned' | 'assigned';
    isPublished: boolean;
    enforceSequence: boolean;
  } | null>(null);

  const {
    data: coursePage,
    isLoading: isLoadingCourses,
    isError: didCourseLoadFail,
    isPlaceholderData,
  } = useCourses(queryParams);

  const totalCount = coursePage?.totalCount ?? 0;

  const displayedCourses = coursePage?.items ?? [];

  usePageBoundsReset({ page, pageSize, totalCount, isPlaceholderData, setPage });

  useEffect(() => {
    if (!openActionCourseId) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        actionMenuRef.current &&
        event.target instanceof Node &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setOpenActionCourseId(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openActionCourseId]);

  const handleCreateCourse = useCallback(() => {
    navigate('/courses/new');
  }, [navigate]);

  const handleOpenCourse = useCallback((courseId: string) => {
    setOpenActionCourseId(null);
    navigate(`/courses/${courseId}`);
  }, [navigate]);

  const handleUnavailableAction = useCallback(() => {
    setOpenActionCourseId(null);
  }, []);

  const { mutate: deleteCourse, isPending: isDeleting } = useDeleteCourseMutation({
    onSuccess: () => {
      setDeleteConfirmId(null);
      setCourseActionError(null);
    },
    onError: (error) => {
      // The mutation refreshes eligibility on a refusal, so the row this
      // banner refers to comes back disabled with its reason.
      setCourseActionError(error.message || 'Failed to delete course');
      setDeleteConfirmId(null);
    },
  });

  const handleDeleteClick = useCallback((courseId: string) => {
    // Focus the trigger BEFORE the menu unmounts, so the dialog captures a
    // live element to hand focus back to.
    kebabTriggerRef.current?.focus();
    setOpenActionCourseId(null);
    setCourseActionError(null);
    setDeleteConfirmId(courseId);
  }, []);

  const handleEditTagsClick = useCallback((course: CourseListResponseItem) => {
    // Same focus hand-off as Delete: the trigger outlives the menu, so the
    // dialog captures it as its focus-restore target.
    kebabTriggerRef.current?.focus();
    setOpenActionCourseId(null);
    setCourseActionError(null);
    setEditTagsCourse({ id: course.id, title: course.title || 'Untitled Course' });
  }, []);

  const handleTagsSaved = useCallback(({ changed }: { changed: boolean }) => {
    const title = editTagsCourse?.title;
    setEditTagsCourse(null);
    if (changed && title) showToast(`Tags updated for ${title}.`);
  }, [editTagsCourse, showToast]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmId) deleteCourse(deleteConfirmId);
  }, [deleteConfirmId, deleteCourse]);

  const handleCancelDelete = useCallback(() => {
    if (isDeleting) return;
    setDeleteConfirmId(null);
  }, [isDeleting]);

  const courseToDelete = deleteConfirmId
    ? displayedCourses.find((course) => course.id === deleteConfirmId)
    : null;

  const handleAssignToAccounts = useCallback((
    course: CourseListResponseItem,
    initialFilter: 'notAssigned' | 'assigned' = 'notAssigned'
  ) => {
    setOpenActionCourseId(null);
    setAssignmentPanel({
      id: course.id,
      title: course.title || 'Untitled Course',
      initialFilter,
      isPublished: course.workflowState === 'published',
      // enforceSequence is independent of timedCourse and has no UI yet —
      // always false until the SCORM export flow offers the option.
      enforceSequence: false,
    });
  }, []);

  const handleAssignedAccounts = useCallback((
    _courseId: string,
    _result: AccountAssignmentResult
  ) => {
  }, []);

  return (
    <>
      <div style={styles.page}>
        <AdminPageHeader
          title="Courses"
          description={isStandardContext ? 'Combine lessons into courses, then publish, deliver and assign them.' : undefined}
          actions={isStandardContext && (
            <button
              type="button"
              onClick={handleCreateCourse}
              style={styles.addLessonButton}
            >
              + New Course
            </button>
          )}
        />

        <div style={styles.listToolbar}>
          <input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search ID or title..."
            style={styles.filterInput}
            aria-label="Search courses by ID or title"
          />
          <TagFilterControl options={tagFilterOptions} selectedTagIds={tagIds} onChange={setTagIds} />
          <span style={styles.resultsCount}>
            {totalCount} {totalCount === 1 ? 'result' : 'results'}
          </span>
          {hasNonDefaultState && (
            <button type="button" onClick={resetToDefaults} style={styles.resetFiltersButton}>
              Reset
            </button>
          )}
        </div>

        <TagFilterChipRow options={tagFilterOptions} selectedTagIds={tagIds} onChange={setTagIds} />

        {courseActionError && (
          <AdminStatusMessage tone="error" role={null}>{courseActionError}</AdminStatusMessage>
        )}

        <section style={styles.panel}>
          {isLoadingCourses ? (
            <p style={styles.stateText}>Loading courses...</p>
          ) : didCourseLoadFail ? (
            <p style={styles.stateText}>Unable to load courses. Please sign in again.</p>
          ) : displayedCourses.length === 0 ? (
            <p style={styles.stateText}>
              {tagIds.length > 0
                ? `No courses match the selected tags (${describeActiveTagFilters(tagFilterOptions, tagIds)}). `
                : isStandardContext
                  ? 'No courses yet.'
                  : `There are no courses assigned to ${clientContext?.name}.`}
              {tagIds.length > 0 && (
                <button type="button" onClick={() => setTagIds([])} style={styles.resetFiltersButton}>
                  Clear filters
                </button>
              )}
            </p>
          ) : (
            <div style={styles.tableScroll}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <SortableHeader
                      label="ID"
                      sortKey="id"
                      activeKey={sortKey}
                      direction={sortDir}
                      onSort={handleSort}
                      styles={styles}
                    />
                    <SortableHeader
                      label="Title"
                      sortKey="title"
                      activeKey={sortKey}
                      direction={sortDir}
                      onSort={handleSort}
                      styles={styles}
                    />
                    <SortableHeader
                      label="Lifecycle"
                      sortKey="workflowState"
                      activeKey={sortKey}
                      direction={sortDir}
                      onSort={handleSort}
                      styles={styles}
                    />
                    {isStandardContext && (
                      <th style={styles.headerCell}>Assigned To</th>
                    )}
                    <SortableHeader
                      label="Created Date"
                      sortKey="createdAt"
                      activeKey={sortKey}
                      direction={sortDir}
                      onSort={handleSort}
                      styles={styles}
                    />
                    {/* NA-398: only while a tag filter is active — an unconditional
                        column would be noise for the normal, unfiltered view. */}
                    {tagIds.length > 0 && (
                      <th style={styles.headerCell}>Tags</th>
                    )}
                    <th style={styles.headerCellActions} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {displayedCourses.map((course) => (
                    <tr key={course.id} style={styles.tableRow}>
                      <td style={styles.idCell}>{formatCourseHumanId(course.numericId, course.id)}</td>
                      <td style={styles.bodyCell}>
                        <button
                          type="button"
                          // NA-210: draft-assigned content in a client context is
                          // view-only — the title opens Preview, not the editor.
                          onClick={() => (!isStandardContext && course.workflowState === 'draft'
                            ? navigate(`/courses/${course.id}/preview`)
                            : handleOpenCourse(course.id))}
                          style={styles.titleButton}
                        >
                          {course.title || 'Untitled Course'}
                        </button>
                        <StandardTitleLine standardTitle={course.standardTitle} style={styles.standardTitleLine} />
                      </td>
                      <td style={styles.bodyCell}>
                        <WorkflowStatusBadge state={deriveBadgeState(course.workflowState, course.publishLifecycle)} />
                      </td>
                      {isStandardContext && (
                        <td style={styles.bodyCell}>
                          <button
                            type="button"
                            onClick={() => handleAssignToAccounts(
                              course,
                              course.assignedAccountIds.length > 0 ? 'assigned' : 'notAssigned'
                            )}
                            style={styles.accountLinkButton}
                          >
                            {formatAssignedTo(course.assignedAccountIds, clientAccounts)}
                          </button>
                        </td>
                      )}
                      <td style={styles.bodyCell}>{formatCourseCreatedAt(course.createdAt)}</td>
                      {tagIds.length > 0 && (
                        <td style={styles.bodyCell}>
                          <TagNameList tags={course.tags ?? []} />
                        </td>
                      )}
                      <td style={styles.actionsCell}>
                        <div
                          style={styles.actionMenuWrapper}
                          ref={openActionCourseId === course.id ? actionMenuRef : null}
                        >
                          <button
                            type="button"
                            onClick={(event) => {
                              kebabTriggerRef.current = event.currentTarget;
                              setOpenActionCourseId((current) => current === course.id ? null : course.id);
                            }}
                            style={styles.actionMenuButton}
                            aria-label={`Open actions for ${course.title || 'Untitled Course'}`}
                            aria-expanded={openActionCourseId === course.id}
                          >
                            <AdminIcon icon={adminActionIcons.more} />
                          </button>
                          {openActionCourseId === course.id && (
                            <AdminRowMenu style={styles.actionMenuPopover}>
                              <button
                                type="button"
                                onClick={() => handleOpenCourse(course.id)}
                                disabled={!isStandardContext && course.workflowState === 'draft'}
                                style={{
                                  ...styles.actionMenuItem,
                                  ...(!isStandardContext && course.workflowState === 'draft'
                                    ? styles.actionMenuItemDisabled
                                    : {}),
                                }}
                              >
                                Edit
                              </button>
                              {isStandardContext && (
                                <button
                                  type="button"
                                  onClick={handleUnavailableAction}
                                  style={styles.actionMenuItem}
                                >
                                  Duplicate
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={handleUnavailableAction}
                                style={styles.actionMenuItem}
                              >
                                Preview
                              </button>
                              {isStandardContext && (
                                <button
                                  type="button"
                                  onClick={() => handleAssignToAccounts(course, 'notAssigned')}
                                  style={styles.actionMenuItem}
                                >
                                  Assign to Accounts
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleEditTagsClick(course)}
                                style={styles.actionMenuItem}
                              >
                                Edit tags
                              </button>
                              {isStandardContext && (() => {
                                // NA-283: Delete stays visible when ineligible so
                                // the reason is discoverable up front, instead of
                                // surfacing as a 409 after the author commits.
                                const blockedReason = describeDeleteBlock(course.deleteEligibility);
                                const deletable = canDelete(course.deleteEligibility);
                                return (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteClick(course.id)}
                                    disabled={!deletable}
                                    title={blockedReason ?? undefined}
                                    aria-describedby={blockedReason ? `${deleteReasonIdPrefix}-${course.id}` : undefined}
                                    style={{
                                      ...styles.actionMenuItem,
                                      ...styles.actionMenuItemDanger,
                                      ...(deletable ? {} : styles.actionMenuItemDisabled),
                                    }}
                                  >
                                    Delete
                                    {blockedReason && (
                                      <span
                                        id={`${deleteReasonIdPrefix}-${course.id}`}
                                        style={styles.actionMenuItemReason}
                                      >
                                        {blockedReason}
                                      </span>
                                    )}
                                  </button>
                                );
                              })()}
                            </AdminRowMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <ListPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          isPlaceholderData={isPlaceholderData}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      <DeleteConfirmModal
        presentation="admin"
        isOpen={deleteConfirmId !== null}
        title="Delete Course?"
        itemName={courseToDelete?.title || 'Untitled Course'}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <EditTagsDialog
        isOpen={editTagsCourse !== null}
        contentType="course"
        itemId={editTagsCourse?.id ?? ''}
        itemTitle={editTagsCourse?.title ?? ''}
        onClose={() => setEditTagsCourse(null)}
        onSaved={handleTagsSaved}
      />

      <Toast message={toastMessage} />

      {assignmentPanel && (
        <AccountAssignmentPanel
          contentType="course"
          contentId={assignmentPanel.id}
          title={assignmentPanel.title}
          initialFilter={assignmentPanel.initialFilter}
          isPublished={assignmentPanel.isPublished}
          enforceSequence={assignmentPanel.enforceSequence}
          onClose={() => setAssignmentPanel(null)}
          onAssigned={(result) => handleAssignedAccounts(assignmentPanel.id, result)}
        />
      )}
    </>
  );
}
